import React, { useMemo } from "react";
import { shallowEqual } from "react-redux";
import usePersonalization from "../../../../hooks/customize/usePersonalization";
import type { BuildAndBuyDispatchType } from "../../../../redux/store";
import { useBnbSelector } from "../../../../redux/store";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import { BnbLabel, getBnbOptionalLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import * as Type from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Type";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import * as Text from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/Text";
import * as Message from "../../../../../../../common-deprecated/styles/v2/lexus/components/Message";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import Icon, { IconCart } from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import DynamicItemSlider from "../../common/DynamicItemSlider";
import * as PersonalizationStyles from "./styles/Personalisation";
import Price from "../../common/price/Price";
import CarDataConsolidator from "../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import WinterWheelsPanel from "./WinterWheelsPanel";
import Popover from "../../../../../../../common-deprecated/components/popover/aem/lexus/Popover";
import { generateImageSet, tyreInfoBnbLabelSelector } from "../../../../utils/typedUtils";
import useGetBuildSpinnerFrame from "../../../../hooks/shared/spin/useGetBuildSpinnerFrame";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { trackCustomizePersonalizationItem, trackTabPersonalization } from "../../../../utils/tracking";
import { showNextGenInteriorDisclaimer } from "../../../../utils/settings/bnbSettingUtils";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";
import { isOnlinePurchaseAvailable } from "../../../../../../shared-logic/utils/basketUtils";
import { MODAL_DETAILED_TYRE_INFO } from "../../../../../../../shared-logic/utils/tyreInfoContants";

const Personalization = (): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const { onSwitch, value, options, selectItem } = usePersonalization();
    const activeTab = useBnbSelector((state) => state.utils.activePersonalization);
    const showWinterWheels = useBnbSelector(
        (state) =>
            CarDataConsolidator.getWinterWheels(state.items.winterWheels, state.basket.colour, state.basket.upholstery)
                .length > 0 && state.utils.activePersonalization === "wheel",
    );
    const showNextGenInteriorDisclaimerSetting = useBnbSelector(
        (state) =>
            showNextGenInteriorDisclaimer(state.bnbSettings, state.commonSettings, state.car.model.id) &&
            getBnbOptionalLabel(
                { commonSettings: state.commonSettings, bnbSettings: state.bnbSettings },
                "nextGenInteriorDisclaimer",
            ),
    );
    const visibleInTopSpin = useBnbSelector((state) => state.car.visibleInTopSpin);
    const tyres = useBnbSelector((state) => CarDataConsolidator.getWheelTyres(state.basket.wheel, state.items.tyres));
    const tyreInfoLabels = useBnbSelector(tyreInfoBnbLabelSelector, shallowEqual);
    const onlinePurchaseNotPossible = useBnbSelector((state) => !isOnlinePurchaseAvailable(state.basket));

    const [interiorTopView, interiorLabel] = useBnbOptionalLabel(["interiorTopView", "interior"]);

    const [tyreFallbackLabel, accessoryDisclaimer, notAvailableOnlineLabel, colourIncludedInPriceLabel] =
        useBnbOptionalLabel([
            "tyreInfoNoEprelFallback",
            "accessoryDisclaimer",
            "notAvailableOnlineBasketWarning",
            "colourIncludedInPrice",
        ]);

    const renderedDescription = `${value.description!}${accessoryDisclaimer ? `<br/>${accessoryDisclaimer}` : ""}`;

    const { getImage, cacheKey } = useGetBuildSpinnerFrame(false);
    const defaultHeight = 160;

    const image = useMemo(() => {
        return generateImageSet(
            (width: number, height: number) => getImage(width, height, 14, { bgColour: "FFFFFF", padding: 0 }),
            328,
            defaultHeight,
        );
    }, [cacheKey]);

    return (
        <>
            <PersonalizationStyles.Wrapper>
                <PersonalizationStyles.Header>
                    <PersonalizationStyles.TabsWrapper>
                        <PersonalizationStyles.Tab
                            isActive={activeTab === "colour"}
                            onClick={() => {
                                if (activeTab !== "colour") {
                                    trackTabPersonalization("customize-colours");
                                    onSwitch("colour");
                                }
                            }}
                        >
                            <BnbLabel label="colour" />
                        </PersonalizationStyles.Tab>
                        <PersonalizationStyles.Tab
                            isActive={activeTab === "wheel"}
                            onClick={() => {
                                if (activeTab !== "wheel") {
                                    trackTabPersonalization("customize-wheels");
                                    onSwitch("wheel");
                                }
                            }}
                        >
                            <BnbLabel label="wheels" />
                        </PersonalizationStyles.Tab>
                        <PersonalizationStyles.Tab
                            isActive={activeTab === "upholstery"}
                            onClick={() => {
                                if (activeTab !== "upholstery") {
                                    trackTabPersonalization("customize-interior");
                                    onSwitch("upholstery");
                                }
                            }}
                        >
                            {visibleInTopSpin && interiorTopView ? interiorTopView : interiorLabel}
                        </PersonalizationStyles.Tab>
                    </PersonalizationStyles.TabsWrapper>
                </PersonalizationStyles.Header>
                <PersonalizationStyles.Body>
                    <Flex.Row justifyContent="flex-start" m={0}>
                        <DynamicItemSlider
                            // Key is needed to surpress the animation when switching between tabs
                            key={activeTab}
                            items={options}
                            activeItem={value}
                            activeItemType={activeTab}
                            selectItem={(item) => {
                                trackCustomizePersonalizationItem(activeTab);
                                selectItem(activeTab, item);
                            }}
                        />
                        <Flex.Col width={{ xs: 1, lg: 1 / 4 }} flexGrow={2} pl={0}>
                            <PersonalizationStyles.Results>
                                <Type.SubMicroText>{value.name}</Type.SubMicroText>
                                {/* Add this check to avoid there's some white-space when price is 0 or empty */}
                                {value.price.cash || value.price.monthly ? (
                                    <PersonalizationStyles.Price>
                                        <Price price={value.price} financeItemId={value.id} />
                                    </PersonalizationStyles.Price>
                                ) : (
                                    ""
                                )}
                                {value.description && (
                                    <div>
                                        <BnbLabel label="moreInfo" />{" "}
                                        <Popover showOnClick content={renderedDescription} useHTML />
                                    </div>
                                )}
                                {showNextGenInteriorDisclaimerSetting && activeTab === "upholstery" && (
                                    <div>
                                        * <BnbLabel label="nextGenInteriorDisclaimer" />
                                    </div>
                                )}
                                {visibleInTopSpin && activeTab === "upholstery" && (
                                    <div>
                                        <img src={image.src} srcSet={image.srcset} sizes="" draggable={false} alt="" />
                                    </div>
                                )}
                                {activeTab === "wheel" && tyreFallbackLabel && (
                                    <Button.Link
                                        color="grey3"
                                        hasIconRight
                                        noUppercase
                                        onClick={(event) => {
                                            event.preventDefault();
                                            dispatch(
                                                showModal({
                                                    type: MODAL_DETAILED_TYRE_INFO,
                                                    tyres,
                                                    tyreInfoLabels,
                                                }),
                                            );
                                        }}
                                    >
                                        <Text.Button>
                                            <BnbLabel label="tyreInfo" />
                                        </Text.Button>
                                        <IconWrapper>
                                            <Icon variant="info" />
                                        </IconWrapper>
                                    </Button.Link>
                                )}
                                {activeTab === "colour" && value.price.cash === 0 && colourIncludedInPriceLabel && (
                                    <Type.SubMicroText>{colourIncludedInPriceLabel}</Type.SubMicroText>
                                )}
                            </PersonalizationStyles.Results>
                        </Flex.Col>
                    </Flex.Row>
                </PersonalizationStyles.Body>

                {showWinterWheels && <WinterWheelsPanel />}

                {onlinePurchaseNotPossible && notAvailableOnlineLabel && (
                    <Message.Info>
                        <IconWrapper>
                            <IconCart />
                        </IconWrapper>
                        {notAvailableOnlineLabel}
                    </Message.Info>
                )}
            </PersonalizationStyles.Wrapper>
        </>
    );
};

export default Personalization;
